"use client";

import { useState } from "react";
import { RotateCcw } from "lucide-react";

type CalculatorValues = {
  leadHours: number;
  leadLoadedRate: number;
  helperHours: number;
  helperLoadedRate: number;
  overheadPerPersonHour: number;
  contingencyPercent: number;
  targetMarginPercent: number;
};

const SAMPLE_VALUES: CalculatorValues = {
  leadHours: 16,
  leadLoadedRate: 50.19,
  helperHours: 16,
  helperLoadedRate: 37.51,
  overheadPerPersonHour: 37.5,
  contingencyPercent: 5,
  targetMarginPercent: 25,
};

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

function money(value: number): string {
  return moneyFormatter.format(Number.isFinite(value) ? value : 0);
}

function NumberField({
  id,
  label,
  value,
  onChange,
  suffix,
  max,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  suffix: string;
  max?: number;
}) {
  return (
    <label htmlFor={id} className="block">
      <span className="text-sm font-semibold text-slate-800">{label}</span>
      <span className="mt-2 flex overflow-hidden border border-slate-300 bg-white focus-within:border-[#0d1726] focus-within:ring-1 focus-within:ring-[#0d1726]">
        <input
          id={id}
          type="number"
          min="0"
          max={max}
          step="0.01"
          value={value}
          onChange={(event) => onChange(Math.max(0, Number(event.target.value) || 0))}
          className="min-h-11 min-w-0 flex-1 bg-transparent px-3 py-2 text-base text-slate-950 outline-none"
        />
        <span className="flex items-center border-l border-slate-200 bg-slate-50 px-3 text-sm text-slate-600">
          {suffix}
        </span>
      </span>
    </label>
  );
}

export function ContractorLaborCostCalculator() {
  const [values, setValues] = useState<CalculatorValues>(SAMPLE_VALUES);

  function update(field: keyof CalculatorValues, value: number) {
    setValues((current) => ({ ...current, [field]: value }));
  }

  const leadLabor = values.leadHours * values.leadLoadedRate;
  const helperLabor = values.helperHours * values.helperLoadedRate;
  const personHours = values.leadHours + values.helperHours;
  const loadedLabor = leadLabor + helperLabor;
  const overhead = personHours * values.overheadPerPersonHour;
  const baseCost = loadedLabor + overhead;
  const contingency = baseCost * (Math.min(values.contingencyPercent, 90) / 100);
  const costBasis = baseCost + contingency;
  const marginDecimal = Math.min(values.targetMarginPercent, 90) / 100;
  const calculatedPrice = costBasis / (1 - marginDecimal);
  const roundedQuote = Math.round(calculatedPrice / 5) * 5;

  return (
    <section
      aria-labelledby="labor-calculator-title"
      className="border border-slate-200 bg-white"
    >
      <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-blue-800">Interactive worksheet</p>
            <h3
              id="labor-calculator-title"
              className="mt-1 font-[family-name:var(--landing-display)] text-xl font-semibold text-slate-950"
            >
              Contractor labor price calculator
            </h3>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Replace the sample loaded rates and overhead with your own numbers. Materials and
              other direct job costs are not included in this labor-only calculator.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setValues(SAMPLE_VALUES)}
            className="inline-flex min-h-11 items-center gap-2 border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0d1726]"
          >
            <RotateCcw className="h-4 w-4" aria-hidden />
            Reset sample
          </button>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        <div className="grid gap-5 sm:grid-cols-2">
          <NumberField
            id="lead-hours"
            label="Lead person-hours"
            value={values.leadHours}
            onChange={(value) => update("leadHours", value)}
            suffix="hours"
          />
          <NumberField
            id="lead-loaded-rate"
            label="Lead loaded hourly cost"
            value={values.leadLoadedRate}
            onChange={(value) => update("leadLoadedRate", value)}
            suffix="$/hour"
          />
          <NumberField
            id="helper-hours"
            label="Helper person-hours"
            value={values.helperHours}
            onChange={(value) => update("helperHours", value)}
            suffix="hours"
          />
          <NumberField
            id="helper-loaded-rate"
            label="Helper loaded hourly cost"
            value={values.helperLoadedRate}
            onChange={(value) => update("helperLoadedRate", value)}
            suffix="$/hour"
          />
          <NumberField
            id="overhead-rate"
            label="Overhead per person-hour"
            value={values.overheadPerPersonHour}
            onChange={(value) => update("overheadPerPersonHour", value)}
            suffix="$/hour"
          />
          <NumberField
            id="contingency-percent"
            label="Contingency"
            value={values.contingencyPercent}
            onChange={(value) => update("contingencyPercent", value)}
            suffix="%"
            max={90}
          />
          <NumberField
            id="margin-percent"
            label="Target gross margin"
            value={values.targetMarginPercent}
            onChange={(value) => update("targetMarginPercent", value)}
            suffix="%"
            max={90}
          />
        </div>

        <div className="mt-7 bg-[#0d1726] p-5 text-white sm:p-6" aria-live="polite">
          <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            <div>
              <p className="text-sm text-slate-300">Total person-hours</p>
              <p className="mt-1 text-xl font-semibold">{personHours.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-300">Loaded labor cost</p>
              <p className="mt-1 text-xl font-semibold">{money(loadedLabor)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-300">Allocated overhead</p>
              <p className="mt-1 text-xl font-semibold">{money(overhead)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-300">Contingency</p>
              <p className="mt-1 text-xl font-semibold">{money(contingency)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-300">Cost basis for price</p>
              <p className="mt-1 text-xl font-semibold">{money(costBasis)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-300">Calculated selling price</p>
              <p className="mt-1 text-xl font-semibold">{money(calculatedPrice)}</p>
            </div>
          </div>
          <div className="mt-6 border-t border-slate-700 pt-5">
            <p className="text-sm font-medium text-blue-300">Rounded fixed-price quote</p>
            <p className="mt-2 text-3xl font-semibold text-white">{money(roundedQuote)}</p>
            <p className="mt-2 text-sm leading-6 text-slate-300">
              Rounded to the nearest $5. Review scope, taxes, direct job costs, and local rules
              before using the result.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
